import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { Upload, Check, FileText, Loader2, Plus, Trash2, Users, Copy } from 'lucide-react';
import { Button } from '../../ui/Button';
import { getOfferings } from '../../../lib/supabase-offerings';
import { supabase } from '../../../lib/supabase';
import { htmlToPdfBlob, generatePrintableHtml } from '../../../lib/document-compiler';
export function PublishTab({ templateId, documentInstanceId, generatedMarkdown = '', templateName = 'Document', onPreview }) {
    const [offeringId, setOfferingId] = useState('');
    const [offerings, setOfferings] = useState([]);
    const [isLoadingOfferings, setIsLoadingOfferings] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [publishStatus, setPublishStatus] = useState('idle');
    const [documentType, setDocumentType] = useState('other');
    const [documentName, setDocumentName] = useState(templateName);
    // Assignment configuration
    const [assignees, setAssignees] = useState([]);
    const [signingOrderEnabled, setSigningOrderEnabled] = useState(false);
    const [signingOrderType, setSigningOrderType] = useState('sequential');
    const [expirationDays, setExpirationDays] = useState(30);
    // Published data
    const [publishedAssignments, setPublishedAssignments] = useState([]);
    const [publishedDocumentId, setPublishedDocumentId] = useState(null);
    // Manual PDF upload
    const [manualPdfFile, setManualPdfFile] = useState(null);
    const [useGeneratedPdf, setUseGeneratedPdf] = useState(true);
    // Load offerings from Supabase
    useEffect(() => {
        loadOfferings();
    }, []);
    // Update template name when prop changes
    useEffect(() => {
        setDocumentName(templateName);
    }, [templateName]);
    const loadOfferings = async () => {
        setIsLoadingOfferings(true);
        try {
            const data = await getOfferings();
            setOfferings(data || []);
        }
        catch (error) {
            console.error('Error loading offerings:', error);
        }
        finally {
            setIsLoadingOfferings(false);
        }
    };
    const addAssignee = () => {
        const newAssignee = {
            id: `assignee-${Date.now()}`,
            email: '',
            name: '',
            role: 'investor',
            signingOrder: signingOrderEnabled ? assignees.length + 1 : null
        };
        setAssignees([...assignees, newAssignee]);
    };
    const removeAssignee = (id) => {
        const updated = assignees.filter(a => a.id !== id);
        // Renumber signing orders if enabled
        if (signingOrderEnabled) {
            updated.forEach((a, index) => {
                a.signingOrder = index + 1;
            });
        }
        setAssignees(updated);
    };
    const updateAssignee = (id, updates) => {
        setAssignees(assignees.map(a => a.id === id ? { ...a, ...updates } : a));
    };
    const toggleSigningOrder = (enabled) => {
        setSigningOrderEnabled(enabled);
        if (enabled) {
            // Assign sequential order
            setAssignees(assignees.map((a, index) => ({ ...a, signingOrder: index + 1 })));
        }
        else {
            // Remove order
            setAssignees(assignees.map(a => ({ ...a, signingOrder: null })));
        }
    };
    const generateCompletionToken = () => {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        let token = btoa(String.fromCharCode.apply(null, Array.from(array)));
        // Make URL-safe
        token = token.replace(/\//g, '_').replace(/\+/g, '-').replace(/=/g, '');
        return token;
    };
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert('Copied to clipboard!');
    };
    const handlePublish = async () => {
        // Validation
        if (!offeringId.trim()) {
            alert('Please select an offering');
            return;
        }
        if (!documentName.trim()) {
            alert('Please enter a document name');
            return;
        }
        // Validate PDF source - only require markdown if generating PDF
        if (useGeneratedPdf) {
            if (!generatedMarkdown || generatedMarkdown.trim() === '') {
                alert('Please generate a document first before publishing');
                return;
            }
        }
        else {
            // If uploading manual PDF, validate file is selected
            if (!manualPdfFile) {
                alert('Please select a PDF file to upload');
                return;
            }
        }
        // Validate assignees (only if any are added)
        for (const assignee of assignees) {
            if (!assignee.email.trim()) {
                alert('Please enter email for all assignees');
                return;
            }
            if (!assignee.email.includes('@')) {
                alert(`Invalid email: ${assignee.email}`);
                return;
            }
        }
        setIsPublishing(true);
        setPublishStatus('idle');
        try {
            console.log('[PublishTab] Publishing document with assignments:', {
                product_id: offeringId,
                document_name: documentName,
                assignee_count: assignees.length,
                signing_order_enabled: signingOrderEnabled
            });
            // Step 0: Prepare PDF - either from manual upload or generate from markdown
            let pdfUrl = '';
            let pdfFileName = '';
            let pdfBlob = null;
            if (manualPdfFile && !useGeneratedPdf) {
                // Use manually uploaded PDF
                pdfBlob = manualPdfFile;
                // Extract filename without extension and add timestamp
                const fileNameWithoutExt = manualPdfFile.name.replace(/\.pdf$/i, '').replace(/[^a-z0-9_-]/gi, '_');
                pdfFileName = `${fileNameWithoutExt}_${Date.now()}.pdf`;
                console.log('[PublishTab] Using manually uploaded PDF:', {
                    originalName: manualPdfFile.name,
                    fileName: pdfFileName,
                    fileSize: pdfBlob.size
                });
            }
            else if (useGeneratedPdf) {
                // Generate PDF from markdown
                try {
                    // Generate printable HTML from markdown
                    const printableHtml = generatePrintableHtml(generatedMarkdown, documentName, false);
                    // Convert HTML to PDF Blob
                    pdfBlob = await htmlToPdfBlob(printableHtml, documentName);
                    pdfFileName = `${documentName.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
                    console.log('[PublishTab] PDF generated successfully:', {
                        fileName: pdfFileName,
                        fileSize: pdfBlob.size
                    });
                }
                catch (pdfError) {
                    console.warn('[PublishTab] PDF generation failed, continuing with markdown only:', pdfError);
                    // Non-fatal error, continue with markdown-only
                }
            }
            // Step 1: Upload markdown document to storage (optional if using manual PDF)
            let markdownUploadData = { path: '' };
            let markdownUrl = '';
            if (generatedMarkdown && generatedMarkdown.trim()) {
                const markdownBlob = new Blob([generatedMarkdown], { type: 'text/markdown' });
                const markdownFileName = `${documentName.replace(/\s+/g, '_')}_${Date.now()}.md`;
                const { data: mdUploadData, error: markdownUploadError } = await supabase
                    .storage
                    .from('product-documents')
                    .upload(`${offeringId}/${markdownFileName}`, markdownBlob, {
                    contentType: 'text/markdown',
                    upsert: false
                });
                if (markdownUploadError) {
                    console.warn('[PublishTab] Markdown upload failed:', markdownUploadError);
                    // Non-fatal, continue with PDF-only
                }
                else {
                    markdownUploadData = mdUploadData;
                    const { data: mdUrlData } = supabase
                        .storage
                        .from('product-documents')
                        .getPublicUrl(mdUploadData.path);
                    markdownUrl = mdUrlData.publicUrl;
                    console.log('[PublishTab] Markdown uploaded:', markdownUrl);
                }
            }
            else if (useGeneratedPdf) {
                // If generating PDF, we need markdown
                throw new Error('Generated markdown is required when using auto-generated PDF');
            }
            // Step 1b: Upload PDF if generated or uploaded
            let pdfPublicUrl = '';
            if (pdfBlob) {
                try {
                    console.log('[PublishTab] Uploading PDF file:', {
                        path: `${offeringId}/${pdfFileName}`,
                        size: pdfBlob.size,
                        type: pdfBlob.type
                    });
                    const { data: pdfUploadData, error: pdfUploadError } = await supabase
                        .storage
                        .from('product-documents')
                        .upload(`${offeringId}/${pdfFileName}`, pdfBlob, {
                        contentType: 'application/pdf',
                        upsert: false
                    });
                    if (pdfUploadError) {
                        throw new Error(`PDF upload error: ${pdfUploadError.message}`);
                    }
                    if (!pdfUploadData || !pdfUploadData.path) {
                        throw new Error('PDF upload returned empty data');
                    }
                    const { data: pdfUrlData } = supabase
                        .storage
                        .from('product-documents')
                        .getPublicUrl(pdfUploadData.path);
                    if (!pdfUrlData || !pdfUrlData.publicUrl) {
                        throw new Error('Failed to get public URL for PDF');
                    }
                    pdfPublicUrl = pdfUrlData.publicUrl;
                    console.log('[PublishTab] PDF uploaded successfully:', {
                        path: pdfUploadData.path,
                        url: pdfPublicUrl
                    });
                }
                catch (pdfUploadErr) {
                    const errorMsg = pdfUploadErr instanceof Error ? pdfUploadErr.message : String(pdfUploadErr);
                    console.error('[PublishTab] PDF upload failed:', errorMsg);
                    // Only throw if this was a manual PDF upload (not optional auto-generated)
                    if (!useGeneratedPdf) {
                        throw new Error(`Failed to upload PDF: ${errorMsg}`);
                    }
                    // For auto-generated PDF, continue with markdown as fallback
                    console.warn('[PublishTab] Falling back to markdown URL');
                }
            }
            else if (!useGeneratedPdf) {
                // Manual PDF upload was selected but no file was prepared
                throw new Error('PDF file not found. Please select a PDF file and try again.');
            }
            // Step 2: Determine the document URL (prefer PDF, fallback to markdown)
            let documentUrl = '';
            if (pdfPublicUrl) {
                documentUrl = pdfPublicUrl;
                console.log('[PublishTab] Using PDF URL for document download');
            }
            else if (markdownUrl) {
                documentUrl = markdownUrl;
                console.log('[PublishTab] Using markdown URL for document download');
            }
            else {
                throw new Error('Failed to upload document - neither PDF nor markdown URL available');
            }
            // Step 3: Create document instance - only if there are assignees
            let instanceData = null;
            let createdAssignments = [];
            if (assignees.length > 0) {
                const { data: newInstanceData, error: instanceError } = await supabase
                    .from('document_instances')
                    .insert({
                    template_id: templateId,
                    template_version: '1.0',
                    offering_id: offeringId,
                    status: 'pending_signatures',
                    signing_order_enabled: signingOrderEnabled,
                    public_webform_enabled: false,
                    completion_view_preference: 'both'
                })
                    .select()
                    .single();
                if (instanceError) {
                    throw new Error(`Failed to create document instance: ${instanceError.message}`);
                }
                instanceData = newInstanceData;
                console.log('[PublishTab] Document instance created:', instanceData);
                // Step 4: Create signing sequence if enabled
                if (signingOrderEnabled) {
                    const steps = assignees.map((a, index) => ({
                        order: index + 1,
                        role: a.role,
                        description: `${a.role} signs`
                    }));
                    const { error: seqError } = await supabase
                        .from('signing_sequences')
                        .insert({
                        document_instance_id: instanceData.id,
                        enabled: true,
                        sequence_type: signingOrderType,
                        steps: steps,
                        auto_advance: true,
                        allow_decline: false
                    });
                    if (seqError) {
                        console.error('[PublishTab] Signing sequence error:', seqError);
                        // Non-fatal, continue
                    }
                }
                // Step 5: Create assignments with completion tokens
                const expiresAt = new Date();
                expiresAt.setDate(expiresAt.getDate() + expirationDays);
                const assignmentPromises = assignees.map(async (assignee) => {
                    const token = generateCompletionToken();
                    const completionLink = `${window.location.origin}/complete/${token}`;
                    const { data, error } = await supabase
                        .from('document_assignments')
                        .insert({
                        document_instance_id: instanceData.id,
                        assignee_email: assignee.email,
                        assignee_name: assignee.name || assignee.email,
                        role: assignee.role,
                        signing_order: assignee.signingOrder,
                        completion_token: token,
                        completion_link: completionLink,
                        status: 'pending',
                        expires_at: expiresAt.toISOString(),
                        invitation_sent: false
                    })
                        .select()
                        .single();
                    if (error) {
                        console.error('[PublishTab] Assignment error:', error);
                        throw new Error(`Failed to create assignment for ${assignee.email}: ${error.message}`);
                    }
                    return {
                        assignee_email: assignee.email,
                        assignee_name: assignee.name || assignee.email,
                        role: assignee.role,
                        completion_link: completionLink,
                        completion_token: token,
                        assignment_id: data.id
                    };
                });
                createdAssignments = await Promise.all(assignmentPromises);
                console.log('[PublishTab] Assignments created:', createdAssignments.length);
            }
            else {
                console.log('[PublishTab] No assignees - skipping document instance creation');
            }
            // Step 6: Create entry in product_documents table
            const { data: docData, error: docError } = await supabase
                .from('product_documents')
                .insert({
                product_id: offeringId,
                document_name: documentName,
                document_type: documentType,
                document_url: documentUrl,
                file_size: pdfBlob ? pdfBlob.size : markdownBlob.size,
                is_public: true,
                sort_order: 0
            })
                .select()
                .single();
            if (docError) {
                throw new Error(`Failed to save document metadata: ${docError.message}`);
            }
            console.log('[PublishTab] Document published successfully');
            // Step 7: Send invitation emails (only if there are assignees)
            if (createdAssignments.length > 0) {
                const expiresAt = new Date();
                expiresAt.setDate(expiresAt.getDate() + expirationDays);
                const emailPromises = createdAssignments.map(async (assignment) => {
                    try {
                        const { data, error } = await supabase.functions.invoke('send-document-invitation', {
                            body: {
                                to: assignment.assignee_email,
                                name: assignment.assignee_name,
                                document_name: documentName,
                                completion_link: assignment.completion_link,
                                expires_at: expiresAt.toISOString(),
                                assignment_id: assignment.assignment_id
                            }
                        });
                        if (error) {
                            console.error('[PublishTab] Email error for', assignment.assignee_email, error);
                            // Non-fatal, continue with other emails
                        }
                        else {
                            console.log('[PublishTab] Email sent to', assignment.assignee_email, data);
                        }
                    }
                    catch (err) {
                        console.error('[PublishTab] Email sending failed:', err);
                        // Non-fatal, continue
                    }
                });
                // Wait for all emails to be sent (or fail)
                await Promise.allSettled(emailPromises);
            }
            // Save published data for success screen
            setPublishedAssignments(createdAssignments);
            setPublishedDocumentId(instanceData?.id || null);
            setPublishStatus('success');
        }
        catch (error) {
            console.error('[PublishTab] Publish error:', error);
            setPublishStatus('error');
            alert(`Publishing failed: ${error.message || 'Unknown error'}`);
        }
        finally {
            setIsPublishing(false);
        }
    };
    // Success screen
    if (publishStatus === 'success') {
        return (_jsxs("div", { className: "max-w-4xl mx-auto space-y-6", children: [_jsxs("div", { className: "text-center py-8", children: [_jsx("div", { className: "w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center", style: { backgroundColor: '#10b98120' }, children: _jsx(Check, { className: "w-8 h-8", style: { color: '#10b981' } }) }), _jsx("h2", { className: "text-2xl font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "Document Published Successfully!" }), _jsxs("p", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: [documentName, " ", publishedAssignments.length > 0 && `• ${publishedAssignments.length} assignees`] })] }), publishedAssignments.length > 0 && (_jsxs("div", { className: "p-6 rounded-xl", style: {
                        backgroundColor: 'var(--bg-elevated)',
                        border: '1px solid var(--border-color)'
                    }, children: [_jsx("h3", { className: "text-lg font-semibold mb-4", style: { color: 'var(--text-primary)' }, children: "Completion Links" }), _jsx("div", { className: "space-y-4", children: publishedAssignments.map((assignment, index) => (_jsx("div", { className: "p-4 rounded-lg", style: {
                                    backgroundColor: 'var(--bg-base)',
                                    border: '1px solid var(--border-color)'
                                }, children: _jsx("div", { className: "flex items-start justify-between gap-4", children: _jsxs("div", { className: "flex-1", children: [_jsxs("div", { className: "flex items-center gap-2 mb-2", children: [_jsx("p", { className: "text-sm font-medium", style: { color: 'var(--text-primary)' }, children: assignment.assignee_email }), _jsx("span", { className: "text-xs px-2 py-0.5 rounded", style: { backgroundColor: '#D9770620', color: '#D97706' }, children: assignment.role })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("input", { type: "text", value: assignment.completion_link, readOnly: true, className: "flex-1 px-3 py-2 text-xs rounded", style: {
                                                            backgroundColor: 'var(--bg-elevated)',
                                                            border: '1px solid var(--border-color)',
                                                            color: 'var(--text-secondary)'
                                                        } }), _jsx(Button, { variant: "outline", onClick: () => copyToClipboard(assignment.completion_link), style: { borderColor: '#D97706', color: '#D97706' }, children: _jsx(Copy, { className: "w-4 h-4" }) })] })] }) }) }, index))) })] })), publishedAssignments.length === 0 && (_jsx("div", { className: "p-6 rounded-xl text-center", style: {
                        backgroundColor: '#10b98120',
                        border: '1px solid #10b98140'
                    }, children: _jsxs("p", { className: "text-sm", style: { color: '#10b981' }, children: [_jsx("strong", { children: "Document is now live!" }), " It's available for download. No signatures are required."] }) })), publishedAssignments.length > 0 && (_jsx("div", { className: "p-4 rounded-lg", style: {
                        backgroundColor: '#D9770610',
                        border: '1px solid #D9770630'
                    }, children: _jsxs("p", { className: "text-sm", style: { color: '#D97706' }, children: [_jsx("strong", { children: "Next Steps:" }), " Share the completion links with assignees via email or send automated invitations. The document is now live and ready for signatures."] }) })), _jsxs("div", { className: "flex gap-3 justify-end", children: [_jsx(Button, { variant: "outline", onClick: () => {
                                setPublishStatus('idle');
                                setPublishedAssignments([]);
                                setAssignees([]);
                            }, children: "Publish Another Document" }), _jsx(Button, { onClick: () => window.open('/app/legal-documents', '_self'), style: { backgroundColor: '#D97706', color: 'white' }, children: "Go to Live Tab" })] })] }));
    }
    // Main publish form
    return (_jsxs("div", { className: "max-w-4xl mx-auto space-y-6", children: [_jsxs("div", { children: [_jsx("h2", { className: "text-2xl font-semibold", style: { color: 'var(--text-primary)' }, children: "Publish Document with E-Signatures" }), _jsx("p", { className: "text-sm mt-1", style: { color: 'var(--text-secondary)' }, children: "Configure assignees, signing order, and publish to generate completion links" })] }), _jsx("div", { className: "p-8 rounded-xl space-y-6", style: {
                    backgroundColor: 'var(--bg-elevated)',
                    border: '1px solid var(--border-color)'
                }, children: _jsxs("div", { children: [_jsx("h3", { className: "text-lg font-semibold mb-4", style: { color: 'var(--text-primary)' }, children: "Document Information" }), _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-primary)' }, children: "Offering *" }), _jsxs("div", { className: "relative", children: [_jsxs("select", { value: offeringId, onChange: (e) => setOfferingId(e.target.value), disabled: isLoadingOfferings, className: "w-full px-4 py-3 rounded-lg appearance-none", style: {
                                                        backgroundColor: 'var(--bg-base)',
                                                        border: '1px solid var(--border-color)',
                                                        color: 'var(--text-primary)'
                                                    }, children: [_jsx("option", { value: "", children: isLoadingOfferings ? 'Loading offerings...' : 'Select an offering' }), offerings.map((offering) => (_jsxs("option", { value: offering.id, children: [offering.name, offering.ticker_symbol ? ` (${offering.ticker_symbol})` : ''] }, offering.id)))] }), isLoadingOfferings && (_jsx("div", { className: "absolute right-3 top-1/2 -translate-y-1/2", children: _jsx(Loader2, { className: "w-5 h-5 animate-spin", style: { color: 'var(--text-secondary)' } }) }))] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-primary)' }, children: "Document Name *" }), _jsx("input", { type: "text", value: documentName, onChange: (e) => setDocumentName(e.target.value), placeholder: "Enter document name", className: "w-full px-4 py-3 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            } })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-primary)' }, children: "Document Type *" }), _jsxs("select", { value: documentType, onChange: (e) => setDocumentType(e.target.value), className: "w-full px-4 py-3 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            }, children: [_jsx("option", { value: "subscription_agreement", children: "Subscription Agreement" }), _jsx("option", { value: "ppm", children: "Private Placement Memorandum" }), _jsx("option", { value: "operating_agreement", children: "Operating Agreement" }), _jsx("option", { value: "other", children: "Other" })] })] })] }), _jsxs("div", { className: "p-6 rounded-lg", style: {
                                backgroundColor: 'var(--bg-base)',
                                border: '1px solid var(--border-color)'
                            }, children: [_jsx("h4", { className: "font-medium mb-4", style: { color: 'var(--text-primary)' }, children: "PDF Document" }), _jsxs("div", { className: "space-y-4", children: [_jsxs("label", { className: "flex items-center gap-3 cursor-pointer", children: [_jsx("input", { type: "radio", name: "pdf-source", checked: useGeneratedPdf, onChange: () => {
                                                        setUseGeneratedPdf(true);
                                                        setManualPdfFile(null);
                                                    }, className: "w-4 h-4" }), _jsx("span", { style: { color: 'var(--text-primary)' }, children: "Generate PDF from markdown (Recommended)" })] }), _jsxs("label", { className: "flex items-center gap-3 cursor-pointer", children: [_jsx("input", { type: "radio", name: "pdf-source", checked: !useGeneratedPdf, onChange: () => setUseGeneratedPdf(false), className: "w-4 h-4" }), _jsx("span", { style: { color: 'var(--text-primary)' }, children: "Upload PDF manually" })] })] }), !useGeneratedPdf && (_jsxs("div", { className: "mt-4", children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-primary)' }, children: "Select PDF File *" }), _jsx("input", { type: "file", accept: ".pdf", onChange: (e) => setManualPdfFile(e.target.files?.[0] || null), className: "w-full px-4 py-3 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            } }), manualPdfFile && (_jsxs("p", { className: "text-sm mt-2", style: { color: '#10B981' }, children: ["\u2713 Selected: ", manualPdfFile.name, " (", (manualPdfFile.size / 1024 / 1024).toFixed(2), " MB)"] }))] }))] })] }) }), _jsxs("div", { className: "p-8 rounded-xl space-y-6", style: {
                    backgroundColor: 'var(--bg-elevated)',
                    border: '1px solid var(--border-color)'
                }, children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsxs("h3", { className: "text-lg font-semibold", style: { color: 'var(--text-primary)' }, children: ["Assignees ", _jsx("span", { style: { color: 'var(--text-secondary)', fontWeight: 'normal' }, children: "(Optional)" })] }), _jsx("p", { className: "text-sm mt-1", style: { color: 'var(--text-secondary)' }, children: "Add people who need to complete this document (leave empty to skip e-signature)" })] }), _jsxs(Button, { onClick: addAssignee, style: { backgroundColor: '#D97706', color: 'white' }, children: [_jsx(Plus, { className: "w-4 h-4 mr-2" }), "Add Assignee"] })] }), assignees.length === 0 ? (_jsxs("div", { className: "text-center py-8 rounded-lg", style: {
                            backgroundColor: 'var(--bg-base)',
                            border: '1px dashed var(--border-color)'
                        }, children: [_jsx(Users, { className: "w-12 h-12 mx-auto mb-3", style: { color: 'var(--text-secondary)' } }), _jsx("p", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: "No assignees added. You can publish without signatures or click \"Add Assignee\" to add signers." })] })) : (_jsx("div", { className: "space-y-4", children: assignees.map((assignee, index) => (_jsx("div", { className: "p-4 rounded-lg", style: {
                                backgroundColor: 'var(--bg-base)',
                                border: '1px solid var(--border-color)'
                            }, children: _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-4 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-xs font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Email *" }), _jsx("input", { type: "email", value: assignee.email, onChange: (e) => updateAssignee(assignee.id, { email: e.target.value }), placeholder: "email@example.com", className: "w-full px-3 py-2 rounded text-sm", style: {
                                                    backgroundColor: 'var(--bg-elevated)',
                                                    border: '1px solid var(--border-color)',
                                                    color: 'var(--text-primary)'
                                                } })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-xs font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Name (Optional)" }), _jsx("input", { type: "text", value: assignee.name, onChange: (e) => updateAssignee(assignee.id, { name: e.target.value }), placeholder: "John Doe", className: "w-full px-3 py-2 rounded text-sm", style: {
                                                    backgroundColor: 'var(--bg-elevated)',
                                                    border: '1px solid var(--border-color)',
                                                    color: 'var(--text-primary)'
                                                } })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-xs font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Role" }), _jsxs("select", { value: assignee.role, onChange: (e) => updateAssignee(assignee.id, { role: e.target.value }), className: "w-full px-3 py-2 rounded text-sm", style: {
                                                    backgroundColor: 'var(--bg-elevated)',
                                                    border: '1px solid var(--border-color)',
                                                    color: 'var(--text-primary)'
                                                }, children: [_jsx("option", { value: "investor", children: "Investor" }), _jsx("option", { value: "company", children: "Company" }), _jsx("option", { value: "compliance", children: "Compliance" }), _jsx("option", { value: "admin", children: "Admin" })] })] }), _jsxs("div", { className: "flex items-end gap-2", children: [signingOrderEnabled && (_jsxs("div", { className: "flex-1", children: [_jsx("label", { className: "block text-xs font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Order" }), _jsx("div", { className: "px-3 py-2 rounded text-sm text-center", style: {
                                                            backgroundColor: '#D9770620',
                                                            color: '#D97706',
                                                            fontWeight: '600'
                                                        }, children: index + 1 })] })), _jsx(Button, { variant: "ghost", onClick: () => removeAssignee(assignee.id), style: { color: '#ef4444' }, children: _jsx(Trash2, { className: "w-4 h-4" }) })] })] }) }, assignee.id))) })), _jsx("div", { className: "p-4 rounded-lg", style: {
                            backgroundColor: 'var(--bg-base)',
                            border: '1px solid var(--border-color)'
                        }, children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx("input", { type: "checkbox", checked: signingOrderEnabled, onChange: (e) => toggleSigningOrder(e.target.checked), className: "mt-1" }), _jsxs("div", { className: "flex-1", children: [_jsx("p", { className: "text-sm font-medium mb-1", style: { color: 'var(--text-primary)' }, children: "Enable Signing Order" }), _jsx("p", { className: "text-xs mb-3", style: { color: 'var(--text-secondary)' }, children: "Require assignees to complete the document in a specific order" }), signingOrderEnabled && (_jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-xs font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Order Type" }), _jsxs("select", { value: signingOrderType, onChange: (e) => setSigningOrderType(e.target.value), className: "w-full px-3 py-2 rounded text-sm", style: {
                                                                backgroundColor: 'var(--bg-elevated)',
                                                                border: '1px solid var(--border-color)',
                                                                color: 'var(--text-primary)'
                                                            }, children: [_jsx("option", { value: "sequential", children: "Sequential (one at a time)" }), _jsx("option", { value: "parallel", children: "Parallel (all at once)" })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-xs font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Link Expiration (days)" }), _jsx("input", { type: "number", value: expirationDays, onChange: (e) => setExpirationDays(parseInt(e.target.value) || 30), min: "1", max: "365", className: "w-full px-3 py-2 rounded text-sm", style: {
                                                                backgroundColor: 'var(--bg-elevated)',
                                                                border: '1px solid var(--border-color)',
                                                                color: 'var(--text-primary)'
                                                            } })] })] }))] })] }) })] }), _jsxs("div", { className: "flex gap-3 justify-end", children: [_jsxs(Button, { variant: "outline", onClick: onPreview, disabled: !generatedMarkdown, children: [_jsx(FileText, { className: "w-4 h-4 mr-2" }), "Preview Document"] }), _jsxs(Button, { onClick: handlePublish, disabled: isPublishing ||
                            !offeringId.trim() ||
                            !documentName.trim() ||
                            (useGeneratedPdf && !generatedMarkdown) ||
                            (!useGeneratedPdf && !manualPdfFile), style: {
                            backgroundColor: isPublishing ||
                                !offeringId.trim() ||
                                !documentName.trim() ||
                                (useGeneratedPdf && !generatedMarkdown) ||
                                (!useGeneratedPdf && !manualPdfFile)
                                ? '#9ca3af'
                                : '#D97706',
                            color: 'white'
                        }, children: [_jsx(Upload, { className: "w-4 h-4 mr-2" }), isPublishing ? 'Publishing...' : 'Publish & Generate Links'] })] })] }));
}
